// src/pages/auth/Login.tsx
import Button from "../../../components/common/button/Button";
import Input from "../../../components/common/input/Input";
import styles from "./Login.module.scss";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { useAuth } from "../../../context/AuthContext";
import { useState, useEffect } from "react";
import { useCurrencies } from "../../../context/CurrenciesContext";
import LocalizedLink from "../../../components/common/localized-link/LocalizedLink";
import { useLocalizedNavigate } from "../../../hooks/useLocalizedNavigate";
import { useTranslation } from "react-i18next";
import AuthService from "../../../api/services/auth.service";
import { setCookie } from "../../../utils/cookies";
import { initiateGoogleOAuth } from "../../../utils/googleOAuth";

const Login = () => {
  const { t } = useTranslation("translation");
  const { isLoggedIn, setEmail, setIsLoggedIn, setUser } = useAuth();
  const { currencyId } = useCurrencies();
  const [emailValue, setEmailValue] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessages, setErrorMessages] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const navigate = useLocalizedNavigate();

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/");
    }
  }, [isLoggedIn, navigate]);

  const syncGuestBasket = async () => {
    const storedBasket = localStorage.getItem("guest_basket");
    if (!storedBasket) return;

    try {
      const basketData = JSON.parse(storedBasket);
      if (!Array.isArray(basketData) || basketData.length === 0) return;

      const jsonData = basketData.map(({ product_variant_id, quantity }) => ({
        product_variant_id,
        quantity,
      }));

      const formData = new FormData();
      formData.append("json_data", JSON.stringify(jsonData));

      await AuthService.setSavedBasket(Number(currencyId), formData);
      localStorage.removeItem("guest_basket");
    } catch (error) {
      console.error("Error syncing guest basket:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrorMessage("");
    setErrorMessages({});

    // Validation
    const errors: typeof errorMessages = {};

    if (!emailValue.trim()) {
      errors.email = t("errors.emailRequired") || "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(emailValue)) {
      errors.email = t("errors.emailInvalid") || "Invalid email format";
    }

    if (!password) {
      errors.password = t("errors.passwordRequired") || "Password is required";
    }

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return;
    }

    setLoading(true);

    try {
      console.log("Login attempt with:", { email: emailValue });

      const formData = new FormData();
      formData.append("email", emailValue);
      formData.append("password", password);

      const response = await AuthService.login(formData);

      console.log("Login response:", response.data);

      if (response.data.access_token) {
        setCookie("LuxMartClientAccessToken", response.data.access_token, 7);

        setEmail(emailValue);
        setIsLoggedIn(true);

        if (response.data.user) {
          setUser(response.data.user);
        } else {
          try {
            const userInfo = await AuthService.getUserInfo();
            if (userInfo.data) {
              setUser(userInfo.data);
            }
          } catch (userError) {
            console.warn("Could not fetch user info:", userError);
          }
        }

        await syncGuestBasket();

        navigate("/");
      } else {
        throw new Error("No access token in response");
      }
    } catch (error: any) {
      console.error("Login error:", error);

      let message = t("common_error") || "An error occurred";

      if (error.response) {
        console.error("Error status:", error.response.status);
        console.error("Error data:", error.response.data);

        if (error.response.status === 401) {
          message =
            t("errors.invalidCredentials") || "Invalid email or password";
        } else if (error.response.status === 403) {
          const errorData = error.response.data;
          const errorText = (
            errorData?.error ||
            errorData?.message ||
            ""
          ).toLowerCase();

          if (
            errorText.includes("verify") ||
            errorText.includes("verification") ||
            errorText.includes("not verified") ||
            errorText.includes("confirm") ||
            errorText.includes("activate") ||
            errorText.includes("otp")
          ) {
            setEmail(emailValue);
            navigate("/auth/otp");
            return;
          } else {
            message =
              t("errors.userNotActive") ||
              "User account is not active. Please verify your email.";
          }
        } else if (error.response.status === 429) {
          message = t("too_many_requests");
        } else if (error.response.status === 404) {
          message =
            "Login service is currently unavailable. Please try again later.";
          console.error(
            "404 Error - Possible endpoint issue. URL:",
            error.config?.url,
          );
        } else if (error.response.data?.error) {
          message = error.response.data.error;
        } else if (error.response.data?.message) {
          message = error.response.data.message;
        } else if (typeof error.response.data === "string") {
          message = error.response.data;
        }
      } else if (error.request) {
        // Request gönderildi ama response alınamadı
        message =
          "No response from server. Please check your internet connection.";
      } else if (error.message) {
        message = error.message;
      }

      setErrorMessage(message);
      setLoading(false);
    }
  };

  // Email ve password değiştiğinde error'ları temizle
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailValue(value);
    if (errorMessages.email) {
      setErrorMessages((prev) => ({ ...prev, email: undefined }));
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (errorMessages.password) {
      setErrorMessages((prev) => ({ ...prev, password: undefined }));
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading) {
      handleSubmit(e as any);
    }
  };

  return (
    <div className={styles.login}>
      <TitleUpdater title={t("login") || "Login"} />
      <div className="container">
        <div className={styles.content}>
          <form
            className={styles.form}
            onSubmit={handleSubmit}
            noValidate
            onKeyPress={handleKeyPress}
          >
            <div className={styles.head}>
              <h1 className={styles.title}>
                {t("login_welcome") || "Welcome Back"}
              </h1>
              <p className={styles.description}>
                {t("login_description") ||
                  "Please enter your credentials to login"}
              </p>

              {errorMessage && (
                <div
                  className={styles.errorMessage}
                  role="alert"
                  aria-live="assertive"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  >
                    <path
                      d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z"
                      fill="#DC2626"
                    />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}
            </div>

            <div className={styles.inputs}>
              <Input
                label={t("email") || "Email"}
                placeholder={t("placeholders.email") || "Enter your email"}
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                value={emailValue}
                onChange={handleEmailChange}
                error={errorMessages.email}
                disabled={loading}
                required
                autoFocus
                aria-label="Email address"
                aria-describedby={
                  errorMessages.email ? "email-error" : undefined
                }
              />

              {errorMessages.email && (
                <div id="email-error" className="visually-hidden">
                  {errorMessages.email}
                </div>
              )}

              <Input
                label={t("password") || "Password"}
                placeholder="••••••••••"
                type="password"
                id="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={handlePasswordChange}
                error={errorMessages.password}
                disabled={loading}
                required
                aria-label="Password"
                aria-describedby={
                  errorMessages.password ? "password-error" : undefined
                }
              />

              {errorMessages.password && (
                <div id="password-error" className="visually-hidden">
                  {errorMessages.password}
                </div>
              )}
            </div>
            <div className={styles.options}>
              <div className={styles.rememberMe}>
                <input type="checkbox" name="rememberMe" id="rememberMe" />
                <label htmlFor="rememberMe" className={styles.rememberMeLabel}>
                  {t("remember_me")}
                </label>
              </div>
              <div className={styles.forget}>
                <LocalizedLink
                  to="/auth/forget-password"
                  className={styles.forgetLink}
                  aria-label="Forgot password"
                >
                  {t("forget_password") || "Forgot password?"}
                </LocalizedLink>
              </div>
            </div>

            <div className={styles.login_btn}>
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
                aria-label={loading ? "Logging in" : "Login"}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("logging_in") || "Logging in..."}
                  </>
                ) : (
                  t("login") || "Login"
                )}
              </Button>
            </div>

            <div className={styles.divider}>
              <div className={styles.dividerLine}></div>
              <span className={styles.dividerText}>{t("or") || "OR"}</span>
              <div className={styles.dividerLine}></div>
            </div>

            <div className={styles.google_btn}>
              <Button
                type="button"
                onClick={initiateGoogleOAuth}
                disabled={loading}
                aria-label="Continue with Google"
                className={styles.googleButton}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t("continue_with_google") || "Continue with Google"}
              </Button>
            </div>

            <div className={styles.footer}>
              <p className={styles.footerText}>
                {t("new_user") || "New user?"}
              </p>
              <LocalizedLink
                to="/auth/register"
                className={styles.signupLink}
                aria-label="Sign up"
              >
                {t("sign_up") || "Sign up"}
              </LocalizedLink>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
